import gql from 'graphql-tag';
import produce from 'immer';
import React from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import DataFrameWarningBanner from 'data-frames/graphql/DataFrameWarningBanner';
import {DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import {DataFrameHeaderMenuOptions} from 'data-frames/header-menu/DataFrameHeaderMenuOptions';
import {PersistedDataFrameMetadataModalProps} from 'data-frames/modals/PersistedDataFrameMetadataModal';
import {DataFrameRenderOverrides} from 'data-frames/table/DataFrameTable';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import DataFrameActionButton from 'data-frames/toolbar/DataFrameActionButton';
import {PrimaryActionRenderFn} from 'data-frames/toolbar/DataFrameActionToolbar';
import {getMetadataModalOverflowActionForDataFrameTable} from 'data-frames/toolbar/toolbar-actions/getMetadataModalOverflowAction';
import {renderAddToAnalysisDropdown} from 'data-frames/toolbar/toolbar-actions/renderAddToAnalysisDropdown';
import DataFrameTableWrapper, {
  DataFrameTableWrapperOverflowOption,
  RowSelectionProps,
} from 'data-frames/wrapper/DataFrameTableWrapper';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {Override} from 'util/Types';

import {DatasetViewQuery_dataset_DatasetNode_dataFrame_DataFrameNode as RawPersistedDatasetDataFrameGraphQL} from '../workspace/__generated/DatasetViewQuery';
import {
  GetPersistedDataFrame,
  GetPersistedDataFrameVariables,
  GetPersistedDataFrame_dataFrame_DataFrameNode as RawPersistedDataFrameGraphQL,
  GetPersistedDataFrame_dataFrame_InaccessibleInfoNode as InaccessibleInfoGraphQL,
} from './__generated/GetPersistedDataFrame';

// eslint doesn't recognize that $limit is used within the PersistedDataFrameGraphQL fragment
// TODO(BNCH-121029): Abstract this to a shared file
export const GET_PERSISTED_DATA_FRAME_QUERY = gql`
  query GetPersistedDataFrame($dataFrameId: StrictString!, $limit: StrictInt!)
  @skipAudit
  @engComponent(name: DATA_FRAMES) {
    dataFrame(id: $dataFrameId) {
      ...InaccessibleInfoGraphQL
      ...PersistedDataFrameGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('PersistedDataFrame').fragment}
`;

type PersistedDataFrameFromDataFrameGraphQL = Override<
  RawPersistedDataFrameGraphQL,
  {dataFrameJson: DataFrame}
>;
type PersistedDataFrameFromDatasetGraphQL = Override<
  RawPersistedDatasetDataFrameGraphQL,
  {dataFrameJson: DataFrame}
>;
export type PersistedDataFrameGraphQL =
  | PersistedDataFrameFromDataFrameGraphQL
  | PersistedDataFrameFromDatasetGraphQL;

interface PersistedDataFrameTableWrapperProps {
  actionOrigin: ActionOrigin;
  rowSelectionProps?: RowSelectionProps;
  renderIsExcludedColumn?: boolean;
  /** Optional extra overflow menu options.
   *
   * These will be rendered above the shared overflow menu options defined by this component.
   */
  additionalOverflowOptions?: Array<DataFrameTableWrapperOverflowOption>;
  includeOpenDatasetOption?: boolean;
  /** Additional button options to render in order before the overflow menu button.
   *
   * See `DataFrameActionToolbar`'s `renderElements`.
   */
  additionalActionButtons?: Array<PrimaryActionRenderFn>;
  clientStateId: string;
  dataFrameId: string;
  defaultTableMode?: DataFrameTableMode;
  additionalMetadataModalContent?: Pick<
    PersistedDataFrameMetadataModalProps,
    'additionalGridCells' | 'additionalContent'
  >;
  headerMenuOptions?: DataFrameHeaderMenuOptions;
  isCollapsible?: boolean;
  autoCollapseRowLimit?: number;
}

interface QueriedPersistedDataFrameTableWrapperProps
  extends Omit<PersistedDataFrameTableWrapperProps, 'dataFrameId'> {
  dataFrameNameOverride?: string;
  persistedDataFrame: PersistedDataFrameGraphQL | InaccessibleInfoGraphQL;
}

interface PresentationalPersistedDataFrameTableWrapperProps
  extends Omit<PersistedDataFrameTableWrapperProps, 'dataFrameId'> {
  persistedDataFrame: PersistedDataFrameGraphQL;
  dataFrameRenderOverrides?: DataFrameRenderOverrides;
}

export function PresentationalPersistedDataFrameTableWrapper({
  persistedDataFrame,
  actionOrigin,
  additionalOverflowOptions,
  rowSelectionProps,
  includeOpenDatasetOption = false,
  additionalActionButtons = [],
  clientStateId,
  additionalMetadataModalContent = {},
  headerMenuOptions,
  renderIsExcludedColumn = false,
  defaultTableMode = DataFrameTableMode.TEXT,
  isCollapsible = false,
  dataFrameRenderOverrides = {},
  autoCollapseRowLimit,
}: PresentationalPersistedDataFrameTableWrapperProps): JSX.Element {
  const metadataModalOverflowOption = getMetadataModalOverflowActionForDataFrameTable(
    persistedDataFrame,
    additionalMetadataModalContent
  );

  const actionButtons = additionalActionButtons;
  if (persistedDataFrame.dataset != null) {
    actionButtons.push(
      renderAddToAnalysisDropdown({
        datasetId: persistedDataFrame.dataset?.id,
        dataFrameName: persistedDataFrame.name,
        dataFrameId: persistedDataFrame.id,
        actionOrigin,
      })
    );
    if (includeOpenDatasetOption) {
      actionButtons.push(() => (
        <DataFrameActionButton
          title="Open dataset"
          icon="open"
          onClick={() => {
            navigateToURL(persistedDataFrame.dataset!.url);
          }}
          key="OpenDatasetButton"
        />
      ));
    }
  }

  return (
    <DataFrameTableWrapper
      actionOrigin={actionOrigin}
      additionalOverflowOptions={[...(additionalOverflowOptions ?? []), metadataModalOverflowOption]}
      additionalElements={actionButtons}
      rowSelectionProps={rowSelectionProps}
      banner={
        <DataFrameWarningBanner
          isRandomSubset={false}
          name={persistedDataFrame.name}
          renderedColumns={persistedDataFrame.dataFrameJson.columns.length}
          renderedRows={persistedDataFrame.dataFrameJson.rows.length}
          totalColumns={persistedDataFrame.allColumnsJson.length}
          totalRows={persistedDataFrame.numRows}
        />
      }
      clientStateId={`${clientStateId}-${persistedDataFrame.id}`}
      dataFrame={persistedDataFrame.dataFrameJson}
      dataFrameId={persistedDataFrame.id}
      renderIsExcludedColumn={renderIsExcludedColumn}
      headerMenuOptions={headerMenuOptions}
      defaultTableMode={defaultTableMode}
      isCollapsible={isCollapsible}
      autoCollapseRowLimit={autoCollapseRowLimit}
      dataFrameRenderOverrides={dataFrameRenderOverrides}
    />
  );
}

export function QueriedPersistedDataFrameTableWrapper(
  props: QueriedPersistedDataFrameTableWrapperProps
): JSX.Element {
  const dataFrame = props.persistedDataFrame;
  return isInaccessible(dataFrame) ? (
    <InlineBanner bannerType={InlineBannerType.ERROR} text="You do not have access to this table." />
  ) : (
    <PresentationalPersistedDataFrameTableWrapper
      {...props}
      persistedDataFrame={
        props.dataFrameNameOverride == null
          ? dataFrame
          : produce(dataFrame, (draftDataFrame) => {
              const name = props.dataFrameNameOverride ?? dataFrame.name;
              draftDataFrame.name = name;
              draftDataFrame.dataFrameJson.name = name;
            })
      }
    />
  );
}

export default function PersistedDataFrameTableWrapper(
  props: PersistedDataFrameTableWrapperProps & {
    dataFrameNameOverride?: string;
    rowLimit?: number;
  }
): JSX.Element {
  return (
    <BenchlingQuery<GetPersistedDataFrame, GetPersistedDataFrameVariables>
      query={GET_PERSISTED_DATA_FRAME_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{dataFrameId: props.dataFrameId, limit: props.rowLimit ?? DEFAULT_DATA_FRAME_ROW_LIMIT}}
    >
      {({dataFrame}) => <QueriedPersistedDataFrameTableWrapper {...props} persistedDataFrame={dataFrame} />}
    </BenchlingQuery>
  );
}
